/**
 * PKCE (Proof Key for Code Exchange) Utility Functions
 * 
 * Provides cryptographically secure code verifier and challenge generation
 * for OAuth2 PKCE flow with Web Crypto API and crypto-js fallback.
 */

import CryptoJS from 'crypto-js';

/**
 * Generate cryptographically secure random bytes
 * Uses Web Crypto API when available, falls back to Math.random
 */
function generateRandomBytes(length: number): Uint8Array {
  if (window.crypto && window.crypto.getRandomValues) {
    const randomBytes = new Uint8Array(length);
    window.crypto.getRandomValues(randomBytes);
    return randomBytes;
  }
  
  // Fallback using Math.random (less secure but functional)
  console.warn('Using Math.random fallback for random byte generation. This is less secure.');
  const randomBytes = new Uint8Array(length);
  for (let i = 0; i < length; i++) {
    randomBytes[i] = Math.floor(Math.random() * 256);
  }
  return randomBytes;
}

/**
 * Safe base64 encoding that handles large arrays
 * Provides fallback for browsers with limitations on spread operator
 */
function safeBase64Encode(bytes: Uint8Array): string {
  try {
    // Try the efficient method first
    return btoa(String.fromCharCode(...bytes));
  } catch (e) {
    // Fallback for browsers that can't handle large spread operations
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
      binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
  }
}

/**
 * Generate PKCE code verifier
 * Creates a cryptographically random 43-character URL-safe string
 */
export function generateCodeVerifier(): string {
  const randomBytes = generateRandomBytes(32);
  return safeBase64Encode(randomBytes)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Generate PKCE code challenge from code verifier
 * Uses SHA256 hashing with Web Crypto API or crypto-js fallback
 */
export async function generateCodeChallenge(codeVerifier: string): Promise<string> {
  try {
    // Try modern Web Crypto API first
    if (window.crypto && window.crypto.subtle) {
      const encoder = new TextEncoder();
      const data = encoder.encode(codeVerifier);
      const digest = await window.crypto.subtle.digest('SHA-256', data);
      
      return safeBase64Encode(new Uint8Array(digest))
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
        .replace(/=+$/, '');
    }
  } catch (e) {
    console.warn('Web Crypto API not available, using crypto-js fallback:', e);
  }
  
  // Reliable fallback using crypto-js
  const hash = CryptoJS.SHA256(codeVerifier);
  const hashBytes = new Uint8Array(
    hash.words.flatMap(word => [
      (word >>> 24) & 0xff,
      (word >>> 16) & 0xff,
      (word >>> 8) & 0xff,
      word & 0xff
    ])
  );
  
  return safeBase64Encode(hashBytes)
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

/**
 * Validate PKCE code verifier format
 * Ensures the code verifier meets RFC 7636 requirements
 */
export function validateCodeVerifier(codeVerifier: string): boolean {
  // RFC 7636: code verifier must be 43-128 characters
  // and use only [A-Z] / [a-z] / [0-9] / "-" / "." / "_" / "~"
  const validPattern = /^[A-Za-z0-9\-._~]{43,128}$/;
  return validPattern.test(codeVerifier);
}

/**
 * Validate PKCE code challenge format
 * Ensures the code challenge meets RFC 7636 requirements
 */
export function validateCodeChallenge(codeChallenge: string): boolean {
  // RFC 7636: code challenge must be 43 characters for S256 method
  // and use only [A-Z] / [a-z] / [0-9] / "-" / "_"
  const validPattern = /^[A-Za-z0-9\-_]{43}$/;
  return validPattern.test(codeChallenge);
}
